import { X } from 'lucide-react';
import DangerToast from '@/assets/smartcity/danger-toast.svg';
import InfoToast from '@/assets/smartcity/info-toast.svg';
import SuccessToast from '@/assets/smartcity/success-toast.svg';
import WarningToast from '@/assets/smartcity/warning-toast.svg';

// export type ToastType = 'success' | 'error' | 'warning' | 'info';

const config = {
  success: {
    color: '#22C55E',
    bg: '#F3F6FE',
    icon: SuccessToast,
  },
  error: {
    color: '#EF4444',
    bg: '#F3F6FE',
    icon: DangerToast,
  },
  warning: {
    color: '#F59E0B',
    bg: '#F3F6FE',
    icon: WarningToast,
  },
  info: {
    color: '#1677FF',
    bg: '#F3F6FE',
    icon: InfoToast,
  },
};

export type ToastType = keyof typeof config;

export interface CustomToastAction {
  label: string;
  onClick: () => void;
}

interface CustomToastProps {
  type: ToastType;
  title: string;
  message: string;
  // Optional buttons rendered below the message - each click is a genuine
  // user gesture, so it's the one reliable way to open a browser popup that
  // a synchronous window.open() couldn't (see AmbulanceDispatch.tsx).
  actions?: CustomToastAction[];
  /** Shows a close button; an action click closes the toast too */
  onClose?: () => void;
}

const CustomToast = ({ type, title, message, actions, onClose }: CustomToastProps) => {
  const item = config[type];

  return (
    <div
      style={{
        background: item.bg,
        borderLeftColor: item.color,
      }}
      className="flex w-full items-center flex-row overflow-hidden gap-3 sm:gap-4 rounded-tr-[16px] rounded-br-[16px] sm:rounded-tr-[24px] sm:rounded-br-[24px] border-l-[6px] sm:border-l-[10px] border-solid p-3 sm:p-5 shadow-lg"
    >
      <img src={item.icon} alt="icon" className="w-9 h-9 sm:w-[50px] sm:h-[50px] shrink-0" />

      <div className="flex flex-col w-full min-w-0 gap-1 sm:gap-2">
        <span className="title1bold sm:h5bold break-words">{title}</span>
        <span className="body1 sm:title2 text-db-text-grey break-words">{message}</span>
        {actions && actions.length > 0 && (
          <div className="flex flex-col gap-1">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => {
                  action.onClick();
                  onClose?.();
                }}
                className="title2 text-left text-text-blue-100 underline"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Tutup"
          className="self-start shrink-0 text-db-text-grey transition hover:opacity-70"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
};

export default CustomToast;
